import React, { useCallback, useContext, useEffect, useState } from "react";
import Breadcrumb from "components/molecules/Breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { CompPagination, Container } from "components";
import { AiOutlineCaretDown, AiOutlinePlus } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import {
  API_URL_agent,
  API_URL_department,
  API_URL_group,
} from "constants/api";
import { agentReducers } from "redux/agentSlice";
import { deleteData, getData } from "actions";
import useDebounce from "hooks/useDebounce";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { FaEdit, FaEye, FaFilter, FaList, FaTrash } from "react-icons/fa";
import { SyncLoader } from "react-spinners";
import { capitalFirstLetter } from "utils/capitalFirstLetter";
import moment from "moment";
import { AuthContext } from "context/AuthContext";
import { departmentReducers } from "redux/departmentSlice";
import { groupReducers } from "redux/groupSlice";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Name", field: "first_name" },
  { title: "Username", field: "username" },
  { title: "Email", field: "email" },
  { title: "Status", field: "is_active" },
  { title: "Group Permission", field: "" },
  { title: "Department", field: "primary_dpt" },
  { title: "Created", field: "created_at" },
  { title: "Actions", field: "" },
];

const Agents = () => {
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenDepartment, setIsOpenDepartment] = useState(false);
  const [isOpenGroup, setIsOpenGroup] = useState(false);

  const [addParams, setAddParams] = useState("role_group=!user");
  const [nameOptions, setNameOptions] = useState("Agent Users");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [nameDepartment, setNameDepartment] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [nameGroup, setNameGroup] = useState("");

  const {
    getAgentResult,
    getAgentLoading,
    getAgentError,
    addAgentResult,
    deleteAgentResult,
    getDepartmentResult,
    getGroupResult,
  } = useSelector((state) => ({
    getAgentResult: state.agent.getAgentResult,
    getAgentLoading: state.agent.getAgentLoading,
    getAgentError: state.agent.getAgentError,
    getAgentResult: state.agent.getAgentResult,
    deleteAgentResult: state.agent.deleteAgentResult,
    getDepartmentResult: state.department.getDepartmentResult,
    getGroupResult: state.group.getGroupResult,
  }));

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_agent,
        params,
        { dispatch, redux: agentReducers },
        "GET_AGENT"
      );
    },
    [dispatch]
  );

  const getDepartment = useCallback(
    async (params) => {
      getData(
        API_URL_department,
        params,
        { dispatch, redux: departmentReducers },
        "GET_DEPARTMENT"
      );
    },
    [dispatch]
  );

  const getGroup = useCallback(
    async (params) => {
      getData(
        API_URL_group,
        params,
        { dispatch, redux: groupReducers },
        "GET_GROUP"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_agent + item.id,
      { dispatch, redux: agentReducers },
      "DELETE_AGENT"
    );
  };

  const onEdit = (item) => {
    navigate(`/administrator/staff/agents/edit`, {
      state: {
        item,
      },
    });
  };
  const onView = (item) => {
    navigate(`/administrator/staff/agents/view`, {
      state: {
        item,
      },
    });
  };

  const Options = [
    {
      name: "All Users",
      func: () => {
        setAddParams("");
        setIsOpen(!isOpen);
        setOffset(0);
        setCurrentPage(0);
        setNameOptions("All Users");
      },
    },
    {
      name: "Agent Users",
      func: () => {
        setAddParams("role_group=!user");
        setIsOpen(!isOpen);
        setOffset(0);
        setCurrentPage(0);
        setNameOptions("Agent Users");
      },
    },
  ];

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setIsOpenDepartment(false);
    setIsOpenGroup(false);
  };
  const toggleDropdownDepartment = () => {
    setIsOpen(false);
    setIsOpenDepartment(!isOpenDepartment);
    setIsOpenGroup(false);
  };
  const toggleDropdownGroup = () => {
    setIsOpen(false);
    setIsOpenDepartment(false);
    setIsOpenGroup(!isOpenGroup);
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?group_permission=${filterGroup}&id_department=${filterDepartment}&${addParams}&limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?group_permission=${filterGroup}&id_department=${filterDepartment}&${addParams}&limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [
      limit,
      offset,
      sortOrder,
      sortColumn,
      addParams,
      filterGroup,
      filterDepartment,
    ]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?group_permission=${filterGroup}&id_department=${filterDepartment}&${addParams}&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const action = [
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "View",
      icon: <FaList />,
      func: onView,
      colorlight: "bg-green-500",
      colordark: "dark:bg-green-800",
      hoverlight: "hover:bg-green-600",
      hoverdark: "hover:dark:bg-green-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const handleDepartment = (item) => {
    if (item === false) {
      setFilterDepartment("");
      setNameDepartment("");
      setIsOpenDepartment(!isOpenDepartment);
    } else {
      setFilterDepartment(item.id);
      setNameDepartment(item.name);
      setIsOpenDepartment(!isOpenDepartment);
    }
  };
  const handleGroup = (item) => {
    if (item === false) {
      setFilterGroup("");
      setNameGroup("");
      setIsOpenGroup(!isOpenGroup);
    } else {
      setFilterGroup(item.id);
      setNameGroup(item.name);
      setIsOpenGroup(!isOpenGroup);
    }
  };

  const fetchData = useCallback(() => {
    const params = `?group_permission=${filterGroup}&id_department=${filterDepartment}&${addParams}&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [
    get,
    limit,
    offset,
    sortColumn,
    sortOrder,
    addParams,
    filterGroup,
    filterDepartment,
  ]);

  useEffect(() => {
    fetchData();
    getDepartment("?all=1&limit=${9999999}");
    getGroup("?all=1&limit=${9999999}");
  }, []);

  useEffect(() => {
    fetchData();
  }, [
    limit,
    offset,
    sortColumn,
    sortOrder,
    addParams,
    filterGroup,
    filterDepartment,
  ]);

  useEffect(() => {
    fetchData();
  }, [addParams]);

  useEffect(() => {
    if (addAgentResult) {
      fetchData();
    }
  }, [addAgentResult, fetchData]);

  useEffect(() => {
    if (deleteAgentResult) {
      fetchData();
    }
  }, [deleteAgentResult, fetchData]);

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Agents"}
        excludedPaths={["staff", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow ">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          List of agents
          <div className="col-end-7 flex justify-end">
            <div className="ml-1 relative inline-block flex-col">
              <button
                onClick={toggleDropdown}
                className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
              >
                <FaEye />
                View Options [<span className="text-xs">{nameOptions}</span>]
              </button>

              {isOpen && (
                <div className="absolute z-10 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <ul
                    className="py-1"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="options-menu"
                  >
                    {Options.map((item, index) => (
                      <li
                        key={index}
                        className={`items-center p-1 list-none hover:bg-gray-200 ${
                          nameOptions === item.name ? "bg-blue-200" : ""
                        }`}
                      >
                        <button
                          id={item.name}
                          onClick={() => item.func()}
                          className={`px-2 block `}
                        >
                          {item.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <Link
              to="/administrator/staff/agents/create"
              className="flex items-center ml-4 gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              <AiOutlinePlus />
              Create an agent
            </Link>
          </div>
        </div>

        <div className="flex flex-col md:flex-row text-center md:justify-between align-middle px-4 my-4 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div>
            <div className="ml-1 relative inline-block flex-col text-sm">
              <button
                onClick={toggleDropdownGroup}
                className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
              >
                <FaFilter />
                Group [<span className="text-xs">{nameGroup}</span>]
              </button>

              {isOpenGroup && (
                <div className="absolute z-10 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <ul
                    className="py-1"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="options-menu"
                  >
                    <li className={` p-1 list-none hover:bg-gray-200`}>
                      <p
                        onClick={() => handleGroup(false)}
                        className={`px-2 text-left cursor-pointer`}
                      >
                        None
                      </p>
                    </li>
                    {Array.isArray(getGroupResult.results) &&
                      getGroupResult.results.map((item, index) => (
                        <li
                          key={index}
                          className={` p-1 list-none hover:bg-gray-200 ${
                            filterDepartment === item.id ? "bg-blue-200" : ""
                          }`}
                        >
                          <p
                            onClick={() => handleGroup(item)}
                            className={`px-2 text-left cursor-pointer`}
                          >
                            {item.name}
                          </p>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="ml-1 relative inline-block flex-col text-sm">
              <button
                onClick={toggleDropdownDepartment}
                className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
              >
                <FaFilter />
                Departement [<span className="text-xs">{nameDepartment}</span>]
              </button>

              {isOpenDepartment && (
                <div className="absolute z-10 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <ul
                    className="py-1"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="options-menu"
                  >
                    <li className={` p-1 list-none hover:bg-gray-200`}>
                      <p
                        onClick={() => handleDepartment(false)}
                        className={`px-2 text-left cursor-pointer`}
                      >
                        None
                      </p>
                    </li>
                    {Array.isArray(getDepartmentResult.results) &&
                      getDepartmentResult.results.map((item, index) => (
                        <li
                          key={index}
                          className={` p-1 list-none hover:bg-gray-200 ${
                            filterDepartment === item.id ? "bg-blue-200" : ""
                          }`}
                        >
                          <p
                            onClick={() => handleDepartment(item)}
                            className={`px-2 text-left cursor-pointer`}
                          >
                            {item.name}
                          </p>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="p-1 inline-block align-middle">
              <span className="text-sm mr-2">Search:</span>
              <input
                id="search"
                type="text"
                value={search}
                placeholder="by name & username"
                onChange={(e) => onSearch(e.target.value)}
                className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </div>
        </div>

        {/* <CardContainer title="List of agents"> */}
        <div className="mx-4">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="border border-collapse items-center w-full">
              <thead className="text-left">
                <tr>
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* loading  */}
                {getAgentLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getAgentError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getAgentError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getAgentResult.results && getAgentResult.results.length === 0 && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                        No Data
                      </div>
                    </td>
                  </tr>
                )}

                {getAgentResult.results &&
                  getAgentResult.results.map((item, index) => (
                    <tr key={index}>
                      <td className="p-4 border border-blue-gray-50 w-2/12">
                        <a
                          className="text-blue-700 dark:text-blue-300 text-left"
                          onClick={() => onEdit(item)}
                        >
                          {item.first_name + " " + item.last_name}
                        </a>
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-2/12">
                        <a
                          className="text-blue-700 dark:text-blue-300 text-left"
                          onClick={() => onEdit(item)}
                        >
                          {item.username}
                        </a>
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-2/12 text-left">
                        <p>{item.email}</p>
                      </td>

                      <td className="p-4 border border-blue-gray-50 w-1/12">
                        <p
                          className={`${
                            item.is_active === true
                              ? "text-green-500"
                              : "text-red-600"
                          } `}
                        >
                          {item.is_active === true ? "Active" : "Disable"}
                        </p>
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-2/12 text-center">
                        {item.groups_name.map((itemx, indexx) => (
                          <span key={indexx} className="mx-1 rounded-sm">
                            <span>
                              {capitalFirstLetter(itemx)}
                              {indexx < item.groups_name.length - 1 && ","}
                            </span>
                          </span>
                        ))}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-2/12">
                        {item.primary_dpt_name}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/12">
                        {moment(item.created_at).format("DD/MM/YYYY HH:mm:ss")}
                      </td>
                      <td className="p-4 border border-blue-gray-50 w-1/12">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center gap-1">
                                {action.icon}
                                {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="pt-5 py-11">
            <CompPagination
              handlePageClick={(e) => handlePageClick(e)}
              pageCount={getAgentResult.count > 0 ? getAgentResult.count : 0}
              limit={limit}
              offset={offset}
              currentPage={currentPage}
            />
          </div>
        </div>
        {/* </CardContainer> */}
      </div>
    </Container>
  );
};

export default Agents;
